import { z } from 'zod';

/**
 * ENDS Analysis (vape) report — the ENDS-only sections, each stored in its own
 * `business_analysis` domain column (`mystery_shopper_call`,
 * `google_business_overview`, `fda_warnings`; BAL-8161). Reports of this type
 * are authored offline and uploaded through the admin importer, so every
 * field is tolerant: a missing/partial blob hides the corresponding report
 * section instead of failing the page.
 *
 * Shared sections (tags, summary, merchantDetails, socialMedia, ecosystem,
 * references, inputs) live in their regular `business_analysis` columns and
 * reuse the existing POS/BA schemas — line-of-business included, via
 * `content_analysis.lineOfBusiness` like every other report type.
 */

export const EndsHighlightedReviewSchema = z.object({
  name: z.string().nullish(),
  stars: z.number().nullish(),
  date: z.string().nullish(),
  quote: z.string().nullish(),
  permalink: z.string().nullish(),
});

export const EndsGoogleBusinessOverviewSchema = z.object({
  rating: z.number().nullish(),
  reviewsCount: z.number().nullish(),
  flaggedCount: z.number().nullish(),
  summary: z.string().nullish(),
  chips: z.array(z.object({ label: z.string().nullish() })).nullish(),
  highlightedReviews: z.array(EndsHighlightedReviewSchema).nullish(),
  // Rendered only when present AND an absolute URL — the importer accepts
  // relative values but the UI has no in-app reviews page to point them at.
  analyzedReviewsUrl: z.string().nullish(),
});

export const EndsFdaWarningLetterSchema = z.object({
  productOrBrand: z.string().nullish(),
  recipientEntity: z.string().nullish(),
  actionType: z.string().nullish(),
  actionSubject: z.string().nullish(),
  actionDate: z.string().nullish(),
  status: z.string().nullish(),
  url: z.string().nullish(),
  sourceLabel: z.string().nullish(),
  matchBasis: z.string().nullish(),
  matchConfidence: z.string().nullish(),
});

export const EndsFdaWarningBucketSchema = z.object({
  count: z.number().nullish(),
  note: z.string().nullish(),
  letters: z.array(EndsFdaWarningLetterSchema).nullish(),
  confirmedCount: z.number().nullish(),
  probableCount: z.number().nullish(),
});

export const EndsInspectionRecordSchema = z.object({
  date: z.string().nullish(),
  type: z.string().nullish(),
  subtype: z.string().nullish(),
  flags: z.array(z.string()).nullish(),
  status: z.string().nullish(),
});

export const EndsInspectionHistorySchema = z.object({
  note: z.string().nullish(),
  stats: z
    .object({
      inspections: z.number().nullish(),
      noViolations: z.number().nullish(),
      warningLetters: z.number().nullish(),
      cmps: z.number().nullish(),
      underageSales: z.number().nullish(),
    })
    .nullish(),
  records: z.array(EndsInspectionRecordSchema).nullish(),
});

export const EndsFdaWarningsSchema = z.object({
  againstMerchant: EndsFdaWarningBucketSchema.nullish(),
  againstProductSold: EndsFdaWarningBucketSchema.nullish(),
  findingsNarrative: z.string().nullish(),
  findingsNarrativeSecondary: z.string().nullish(),
  freshnessNote: z.string().nullish(),
  referenceLinks: z
    .array(z.object({ label: z.string().nullish(), url: z.string().nullish() }))
    .nullish(),
  inspectionHistory: EndsInspectionHistorySchema.nullish(),
});

export const ENDS_CALL_STATUSES = ['completed', 'failed', 'cancelled'] as const;
export const ENDS_CALL_OUTCOMES = ['success', 'inconclusive', 'failed'] as const;

// Statuses are authored in another repo (vape-poc) and imported, so a strict
// enum here would take the whole report page down on one unmapped value —
// instead an unrecognized string degrades to 'unknown', which the pill
// renders in the neutral style (BAL-8362). The importer stores whatever the
// producer emitted verbatim rather than nulling it, which makes this schema
// the single owner of unknown-degradation.
const tolerantEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .union([z.enum(values), z.literal('unknown')])
    .nullish()
    .catch('unknown');

// Mystery Shopper Call (BAL-8283, renamed in BAL-8313) — a compliance shopper
// calls the merchant posing as a customer; the call artifacts (recording,
// transcript) are hosted externally and referenced by absolute URL, same rule
// as analyzedReviewsUrl.
export const EndsMysteryShopperCallSchema = z.object({
  // ISO 8601 with offset; `timezoneLabel` is the display label ("EST") since
  // an offset alone can't be rendered back to a named zone.
  dateTime: z.string().nullish(),
  timezoneLabel: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  language: z.string().nullish(),
  callStatus: tolerantEnum(ENDS_CALL_STATUSES),
  // null/absent renders as "N/A" (failed and cancelled calls have no outcome).
  callOutcome: tolerantEnum(ENDS_CALL_OUTCOMES),
  recording: z
    .object({
      url: z.string().nullish(),
      durationSeconds: z.number().nullish(),
    })
    .nullish(),
  transcript: z.object({ url: z.string().nullish() }).nullish(),
  // Markdown, rendered with the shared report markdown components.
  summary: z.string().nullish(),
});

export type EndsMysteryShopperCall = z.infer<typeof EndsMysteryShopperCallSchema>;
export type EndsHighlightedReview = z.infer<typeof EndsHighlightedReviewSchema>;
export type EndsGoogleBusinessOverview = z.infer<typeof EndsGoogleBusinessOverviewSchema>;
export type EndsFdaWarningLetter = z.infer<typeof EndsFdaWarningLetterSchema>;
export type EndsFdaWarningBucket = z.infer<typeof EndsFdaWarningBucketSchema>;
export type EndsInspectionRecord = z.infer<typeof EndsInspectionRecordSchema>;
export type EndsInspectionHistory = z.infer<typeof EndsInspectionHistorySchema>;
export type EndsFdaWarnings = z.infer<typeof EndsFdaWarningsSchema>;
